import {
  BlockOutlined,
  CheckCircleOutlined,
  HourglassEmptyOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { Alert, Chip, Container, Snackbar } from '@mui/material';
import { where } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import MicrospecDataTable from '../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollection, updateData } from '../../../MicrospecDynamicUI/Helpers/dataUtils';

const STATUS_CONFIG = {
  under_review: { label: 'تحت المراجعة', color: 'warning' },
  approved: { label: 'مُوافق عليه', color: 'primary' },
  declined: { label: 'مرفوض', color: 'error' },
};

const TRANSITION_CONFIG = {
  approved: {
    icon: <CheckCircleOutlined fontSize="small" />,
    label: 'موافقة',
    confirmMsg: 'هل تريد الموافقة على هذا العقار؟',
    successMsg: 'تمت الموافقة على العقار',
  },
  declined: {
    icon: <BlockOutlined fontSize="small" />,
    label: 'رفض',
    confirmMsg: 'هل تريد رفض هذا العقار؟',
    successMsg: 'تم رفض العقار',
  },
  under_review: {
    icon: <HourglassEmptyOutlined fontSize="small" />,
    label: 'إعادة للمراجعة',
    confirmMsg: 'هل تريد إعادة هذا العقار للمراجعة؟',
    successMsg: 'تمت إعادة العقار للمراجعة',
  },
};

const AVAILABLE_TRANSITIONS = {
  under_review: ['approved', 'declined'],
  declined: ['approved', 'under_review'],
  approved: ['declined', 'under_review'],
};

const AdminPropertyReviewTable = ({ filterStatus, title, subtitle }) => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const firestore = cloudProvider.firestore;
  const [refreshKey, setRefreshKey] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const wheres = useMemo(
    () => [where('approval_status', '==', filterStatus)],
    [filterStatus]
  );

  const onDataLoad = useCallback(
    async (rows) => {
      if (!firestore?.instance) return rows;
      const db = firestore.instance;

      const catsMap = await new Promise((resolve) => {
        getDataFromCollection(
          (cats) => {
            const map = {};
            (cats || []).forEach((c) => { map[c.id] = c.name || c.label; });
            resolve(map);
          },
          () => {},
          db,
          'categories',
          true,
          50
        );
      });

      return rows.map((row) => ({
        ...row,
        category_name: catsMap[row.category_id] || row.category_id,
      }));
    },
    [firestore?.instance]
  );

  const handleChangeStatus = useCallback(
    async (row, targetStatus) => {
      const cfg = TRANSITION_CONFIG[targetStatus];
      if (!window.confirm(cfg.confirmMsg)) return;
      try {
        await updateData(`properties/${row.id}`, firestore, { approval_status: targetStatus });
        setSnackMsg(cfg.successMsg);
        setSnackOpen(true);
        setRefreshKey((k) => k + 1);
      } catch (err) {
        console.error('Error updating approval_status:', err);
      }
    },
    [firestore]
  );

  const columns = useMemo(
    () => [
      { field: 'title', headerName: 'العنوان', minWidth: 200 },
      { field: 'category_name', headerName: 'التصنيف', minWidth: 120 },
      {
        field: 'price',
        headerName: 'السعر',
        minWidth: 120,
        render: (value, row) =>
          row.price_on_request ? 'عند الطلب' : `₪ ${Number(value || 0).toLocaleString()}`,
      },
      {
        field: 'approval_status',
        headerName: 'الحالة',
        minWidth: 140,
        render: (value) => {
          const cfg = STATUS_CONFIG[value] || STATUS_CONFIG.under_review;
          return (
            <Chip
              component="span"
              size="small"
              label={cfg.label}
              color={cfg.color}
              variant="outlined"
            />
          );
        },
      },
    ],
    []
  );

  const actions = useMemo(() => {
    const transitionKeys = AVAILABLE_TRANSITIONS[filterStatus] || [];
    return [
      {
        icon: <OpenInNewOutlined fontSize="small" />,
        label: 'عرض',
        onClick: (row) => window.open(`/property/${row.id}`, '_blank'),
      },
      ...transitionKeys.map((targetStatus) => {
        const cfg = TRANSITION_CONFIG[targetStatus];
        return {
          icon: cfg.icon,
          label: cfg.label,
          onClick: (row) => handleChangeStatus(row, targetStatus),
        };
      }),
    ];
  }, [filterStatus, handleChangeStatus]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <MicrospecDataTable
        key={refreshKey}
        collection="properties"
        columns={columns}
        wheres={wheres}
        onDataLoad={onDataLoad}
        actions={actions}
        title={{ type: 'Literal', text: title }}
        subtitle={{ type: 'Literal', text: subtitle }}
        searchable
        refreshable
        emptyMessage="لا توجد عقارات في هذه الفئة"
        pageSize={10}
        onRowClick={(row) => window.open(`/property/${row.id}`, '_blank')}
      />
      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24 }}
      >
        <Alert severity="success" variant="filled">
          {snackMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPropertyReviewTable;
